/**
 * Brand Configuration
 * 
 * This file manages brand-specific settings (logo, colors, name)
 * Set REACT_APP_BRAND in .env file to switch brands
 * Supported brands: 'farmstream' | '4gcctv'
 */

import farmstreamLogo from '../assets/images/farmstream_logo.png';
import gcctvLogo from '../assets/images/4gcctv_logo.png';

export type BrandName = 'farmstream' | '4gcctv';

interface BrandConfig {
  name: string;
  displayName: string;
  logo: string;
  colors: {
    primary: string;
    primary100: string;
    primary200: string;
    primary300: string;
    primary400: string;
  };
}

const brandConfigs: Record<BrandName, BrandConfig> = {
  farmstream: {
    name: 'farmstream',
    displayName: 'Farmstream',
    logo: farmstreamLogo,
    colors: {
      primary: '#29a8d3',      // Bright cyan blue
      primary100: '#E6F7FF',
      primary200: '#BAE7FF',
      primary300: '#86CFFB',
      primary400: '#36A3EB'
    }
  },
  '4gcctv': {
    name: '4gcctv',
    displayName: '4GCCTV',
    logo: gcctvLogo,
    colors: {
      primary: '#01b3bf',      // Cyan from 4GCCTV brand
      primary100: '#E6F9FA',
      primary200: '#B3EEF2',
      primary300: '#80E3E9',
      primary400: '#40CBD5'
    }
  }
};

// Get brand from environment variable, default to farmstream
const currentBrand: BrandName = 
  (process.env.REACT_APP_BRAND as BrandName) || 'farmstream';

// Debug logging to verify brand selection
console.log('🎨 Brand Configuration:', {
  REACT_APP_BRAND: process.env.REACT_APP_BRAND,
  currentBrand: currentBrand,
  allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
});

// Validate brand exists
if (!brandConfigs[currentBrand]) {
  console.warn(
    `Invalid brand "${currentBrand}" specified. Falling back to "farmstream".`
  );
}

// Export the current brand configuration
export const brandConfig: BrandConfig = 
  brandConfigs[currentBrand] || brandConfigs.farmstream;

// Export helper to check current brand
export const isBrand = (brand: BrandName): boolean => currentBrand === brand;

export default brandConfig;
